import { useEffect, useRef, useState } from 'react';
import { reactScrollConfigBumpy, scrollToTop } from '~/utils/utils';

enum AnimationCSSClass {
  none = '',
  in = 'in',
  out = 'out',
  downIn = 'down-in',
  downOut = 'down-out',
}

export type AnimateSlideType = ReturnType<
  typeof useSlideAnimation
>['animateSlide'];

export function useSlideAnimation(className: string) {
  const mountedRef = useRef(true);

  const [isSliding, setIsSliding] = useState(false);
  const [slideAnimationClass, setSlideAnimationClass]
    = useState<AnimationCSSClass>(AnimationCSSClass.in);

  const slideClass = slideAnimationClass
    ? `${className}--${slideAnimationClass}`
    : '';

  const animationTimeout = async (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  const animateSlide = async ({
    callback,
    back = false,
  }: {
    callback: () => Promise<void>;
    back?: boolean;
  }) => {
    setIsSliding(true);

    const transitionOut = back
      ? AnimationCSSClass.downOut
      : AnimationCSSClass.out;

    const transitionIn = back ? AnimationCSSClass.downIn : AnimationCSSClass.in;
    setSlideAnimationClass(transitionOut);

    await animationTimeout(400);
    if (!mountedRef.current)
      return;
    scrollToTop(reactScrollConfigBumpy);
    await callback();
    setSlideAnimationClass(transitionIn);
    await animationTimeout(400);

    if (mountedRef.current)
      setIsSliding(false);
    scrollToTop(reactScrollConfigBumpy);
  };

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    animateSlide,
    slideClass,
    isSliding,
  };
}
